import React, { ChangeEvent, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Avatar,
    FormControl,
    FormControlLabel,
    RadioGroup,
    TextField,
    Radio,
    CircularProgress,
    Divider,
    MenuItem,
    Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Person as PersonIcon,
    Brightness4 as DarkModeIcon,
    Language as LanguageIcon,
    CreditCard as CreditCardIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon,
    ArrowCircleRight as ArrowCircleRightIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useSettings } from 'src/@core/hooks/useSettings';
import { Mode } from 'src/@core/layouts/types';
import axios from 'src/services/ApiService';
import * as Yup from 'yup';
import AuthorizeService from 'src/components/api-authorization/AuthorizeService';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Styled components
const PreferenceButton = styled(Button)(({ theme }) => ({
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    textTransform: 'none',
    width: '100%',
    color: theme.palette.text.primary,
    '& .MuiButton-endIcon': {
        marginLeft: 'auto',
    },
}));

const IconWrapper = styled('div')(({ theme }) => ({
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
}));

// Initial Form State
const initialFormState = {
    firstName: '',
    lastName: '',
    email: '',
    imgSrc: '/images/avatars/1.png',
    phoneNumber: '',
};

// Form validation schema
const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is Required'),
    lastName: Yup.string().required('Last Name is Required'),
    email: Yup.string().email('Please enter a valid email address').required('Email is Required'),
});

interface ApiResponse {
    value: UserSubscriptions;
    data: any;
}

interface UserSubscriptions {
    id: number;
    userId: string;
    subscriptionId: number;
    startDate: Date;
    validUpto: number;
    paymentId: number;
    name: string;
    channelsLimit: number;
    channelsPrice: number;
    postsPerMonth: number;
}

const AccountPreferences = () => {
    const [openProfileDialog, setOpenProfileDialog] = useState(false);
    const [openSubDialog, setOpenSubDialog] = useState(false);
    const [openLangDialog, setOpenLangDialog] = useState(false);
    const [openModeDialog, setOpenModeDialog] = useState(false);
    const [imgSrc, setImgSrc] = useState<string>(initialFormState.imgSrc);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [userPlanData, setUserPlanData] = useState<UserSubscriptions | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [language, setLanguage] = useState<string>('English');
    const { settings, saveSettings } = useSettings();
    const [mode, setMode] = useState<Mode>(settings.mode);
    const navigate = useNavigate();

    const handlePersonalInfoOpen = () => {
        setOpenProfileDialog(true);
    };

    const handlePersonalInfoDialogClose = () => {
        setOpenProfileDialog(false);
    };

    const handleSubPlanOpen = () => {
        setOpenSubDialog(true);
    };

    const handleSubscriptionDialogClose = () => {
        setOpenSubDialog(false);
    };

    const handleLanguageDialogOpen = () => {
        setOpenLangDialog(true);
    };

    const handleLanguageDialogClose = () => {
        setOpenLangDialog(false);
    };

    const handleModeDialogOpen = () => {
        setOpenModeDialog(true);
    };

    const handleModeDialogClose = () => {
        setOpenModeDialog(false);
    };

    const handleModeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedMode = event.target.value as Mode;
        saveSettings({ ...settings, mode: selectedMode });
        setMode(selectedMode);
    };

    const handleLanguageChange = (event: SelectChangeEvent) => {
        setLanguage(event.target.value);
    };

    const handleInputImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImgSrc(String(reader.result));
            };
            reader.readAsDataURL(file);
        }
    };

    // Fetch user profile and subscription data
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axios.get('userprofile/GetCurrentUserProfile');
                const { firstName, lastName, email, imgSrc, phoneNumber } = response.data.result;
                formik.setValues({ firstName, lastName, email, imgSrc, phoneNumber });
                setImgSrc(imgSrc);
            } catch (error) {
                console.error('Failed to fetch user profile: ', error);
            }
        };

        const getUserDetails = async () => {
            const response = await AuthorizeService.getUser();
            if (response) {
                setUserId(response.sub);
            } else {
                setUserId(null);
            }
        };

        getUserDetails();
        fetchProfile();
    }, []);

    useEffect(() => {
        const getPlanData = async () => {
            try {
                if (userId) {
                    const response = await axios.get<ApiResponse>(`UserSubscriptions/${userId}`);
                    const data = response.data?.value;
                    if (data) {
                        const validUptoDate = new Date(data.validUpto);
                        const currentDate = new Date();
                        const differenceInMilliseconds = validUptoDate.getTime() - currentDate.getTime();
                        const daysLeft = Math.round(differenceInMilliseconds / (1000 * 3600 * 24));

                        const dataList = {
                            id: data.id,
                            userId: data.userId,
                            subscriptionId: data.subscriptionId,
                            startDate: data.startDate,
                            validUpto: daysLeft,
                            paymentId: data.paymentId,
                            name: data.name,
                            channelsLimit: data.channelsLimit,
                            channelsPrice: data.channelsPrice,
                            postsPerMonth: data.postsPerMonth,
                        };
                        setUserPlanData(dataList);
                    }
                }
            } catch (err: any) {
                console.log(err);
            }
        };

        getPlanData();
    }, [userId]);

    const formik = useFormik({
        initialValues: initialFormState,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                await axios.post('userprofile/SaveCurrentUserProfile', { ...values, imgSrc });
                setSuccessOpen(true); // Open success dialog
            } catch (error) {
                console.error('Failed to submit form: ', error);
            }
            setIsSubmitting(false);
        },
    });

    return (
        <Container sx={{ maxWidth: 'xl', margin: 'auto', padding: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mt: 2 }}>
                Preferences
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}>
                Manage your account, profile information, general preferences and/or change subscription plans
            </Typography>
            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        {/* Profile Information */}
                        <Grid item xs={12}>
                            <PreferenceButton onClick={handlePersonalInfoOpen} startIcon={<PersonIcon />} endIcon={<ChevronRightIcon />}>
                                Profile Information
                            </PreferenceButton>
                        </Grid>

                        {/* General Preferences */}
                        <Grid item xs={12}>
                            <PreferenceButton onClick={handleModeDialogOpen} startIcon={<DarkModeIcon />} endIcon={<ChevronRightIcon />}>
                                Dark Mode
                            </PreferenceButton>
                        </Grid>
                        <Grid item xs={12}>
                            <PreferenceButton onClick={handleLanguageDialogOpen} startIcon={<LanguageIcon />} endIcon={<ChevronRightIcon />}>
                                Language
                            </PreferenceButton>
                        </Grid>

                        {/* Subscriptions & Payments */}
                        <Grid item xs={12}>
                            <PreferenceButton onClick={handleSubPlanOpen} startIcon={<CreditCardIcon />} endIcon={<ChevronRightIcon />}>
                                Subscriptions & Payments
                            </PreferenceButton>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Profile Dialog */}
            <Dialog open={openProfileDialog} onClose={handlePersonalInfoDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Personal Details
                    <IconButton
                        aria-label="close"
                        onClick={handlePersonalInfoDialogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar src={imgSrc} sx={{ width: 80, height: 80, mr: 2 }} />
                            <Button component='label' variant='contained'>
                                Upload New Photo
                                <input
                                    hidden
                                    type='file'
                                    accept='image/png, image/jpeg'
                                    onChange={handleInputImageChange}
                                />
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='First Name'
                                    name='firstName'
                                    onChange={formik.handleChange}
                                    value={formik.values.firstName}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    variant='outlined'
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Last Name'
                                    name='lastName'
                                    onChange={formik.handleChange}
                                    value={formik.values.lastName}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    variant='outlined'
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant='contained' type='submit' disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size={24} /> : 'Save Changes'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dark Mode Dialog */}
            <Dialog open={openModeDialog} onClose={handleModeDialogClose}>
                <DialogTitle>
                    Dark Mode
                    <IconButton
                        aria-label="close"
                        onClick={handleModeDialogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography>Choose how your experience looks for this device.</Typography>
                    <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <RadioGroup value={mode} onChange={handleModeToggle}>
                            <FormControlLabel value="dark" control={<Radio />} label="Always on" />
                            <FormControlLabel value="light" control={<Radio />} label="Always off" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
            </Dialog>

            {/* Language Dialog */}
            <Dialog open={openLangDialog} onClose={handleLanguageDialogClose}>
                <DialogTitle>
                    Select your preferred language
                    <IconButton
                        aria-label="close"
                        onClick={handleLanguageDialogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth>
                        <Select value={language} onChange={handleLanguageChange}>
                            <MenuItem value="English">English</MenuItem>
                            <MenuItem value="French">French</MenuItem>
                            <MenuItem value="Arabic">Arabic</MenuItem>
                            {/* Add more languages if needed */}
                        </Select>
                    </FormControl>
                </DialogContent>
            </Dialog>

            {/* Subscription Dialog */}
            <Dialog open={openSubDialog} onClose={handleSubscriptionDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Current Plan
                    <IconButton
                        aria-label="close"
                        onClick={handleSubscriptionDialogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {userPlanData ? (
                        <>
                            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="h6">{userPlanData.name}</Typography>
                                <Button variant="contained" onClick={() => navigate('/subscriptions')}>
                                    Change Plan
                                </Button>
                            </Grid>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                {userPlanData.validUpto} days are left in your trial
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="body1">Users</Typography>
                                            <Typography variant="h6">1</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="body1">Channels</Typography>
                                            <Typography variant="h6">{userPlanData.channelsLimit}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="body1">Amount</Typography>
                                            <Typography variant="h6">${userPlanData.channelsPrice}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Typography>You are not subscribed to a plan</Typography>
                            <DialogActions>
                                <Button variant='contained' onClick={() => navigate('/subscriptions')}>
                                    Choose the perfect plan
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <Typography>Profile updated successfully!</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuccessOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AccountPreferences;
